import React, { useCallback, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { evidenceDropzone, focusRing, pillAction, pressable } from '../../../styles/visualLanguage';
import { AdvisoryEngagementShell } from './AdvisoryEngagementShell';
import { AdvisoryThinkingState } from './AdvisoryThinkingState';
import { stepPosition } from '../advisoryStepPosition';
import type { AdvisoryPath } from '../advisoryStepPosition';
import { AdvisoryDocumentList } from './AdvisoryDocumentList';
import type { UseAdvisoryDocumentsResult } from '../../../hooks/advisory/useAdvisoryDocuments';
/**
 * AdvisoryEvidenceScreen
 *
 * The last wizard step body. It composes exactly one
 * `AdvisoryEngagementShell` and adds no chrome of its own, following the
 * criteria/key-question editors' own precedent — it never wraps itself in a
 * second shell and mints no navigation beyond its own local hand-off flag.
 * Unlike those two editors it has no Firestore-backed channel at all: there
 * is no `projectId`, no data prop and no channel-branching body, because
 * nothing in the data model backs a file list yet.
 *
 * The panel below the title (`advisory-evidence-dropzone`) is a plain,
 * inert surface: no `role`, no tab stop, no click and no keyboard handler.
 * It carries no drag or drop listeners either — the default action of a
 * dropped file is never cancelled, so a file dragged onto it does whatever
 * the browser normally does. There is nothing to hover or press, so the
 * panel carries no pointer cursor and no hover or drag-over accent
 * treatment. Its own second line states plainly that uploading is not
 * available yet. Nothing renders between the panel and the action row.
 *
 * The terminal action (`advisory-evidence-start`) starts the engagement — S-05.
 * It used to be a dead end recorded as `WP-05-run-execution-start`, on the
 * grounds that no run contract existed; agent-svc's analysis stage is that
 * contract, and the placeholder is retired.
 *
 * This module still imports no thunk, no service and no write-path hook, and
 * that is deliberate: it calls the `onStart` prop and the page that renders it
 * owns the supervisor turn. Keeping the screen presentational is what lets it
 * stay the one wizard body with no `projectId` and no channel-branching.
 *
 * The hand-off lives in this component's own `useState` only — no URL
 * param, no store field and no new `AdvisoryWizardStep` member. It
 * REPLACES the shell entirely (a sibling early return, never a child
 * inside it) with `AdvisoryThinkingState`. Its leave-and-return note stays
 * suppressed: the run itself survives a reload — every V3 turn is a detached
 * Cloud Run Job and the project document records the run — but THIS wait does
 * not, because a fresh mount lands back on the evidence body. What recovers a
 * run in flight is the `analysisRunning` stage derived from the document, not
 * this flag.
 */
export interface AdvisoryEvidenceScreenProps {
  path: AdvisoryPath;
  onBack: () => void;
  /**
   * The engagement's documents, from `useAdvisoryDocuments`. Passed in rather than
   * called here for the same reason as `onStart`: this screen stays presentational,
   * and the page owns both the project id and the collection handle derived from it.
   *
   * `documents.blocked` is what closes the start action, and it is not the same as
   * "a file failed". A document still ingesting when the run starts is a document
   * the engagement never sees — a node reads its collection as it stands when its
   * turn runs — whereas a failed one is terminal and its owner may reasonably start
   * without it.
   */
  documents: UseAdvisoryDocumentsResult;
  /**
   * Starts the engagement (S-05). A callback rather than a supervisor call of
   * this component's own, which keeps the screen presentational: it has no
   * `projectId`, no data prop and no channel-branching body, and the page that
   * renders it already owns both the project id and the one permitted
   * `useSupervisorTurn` call site.
   *
   * Awaited so `handoff` is only entered once the turn has actually been
   * accepted — a rejected lock must land back on this body with the error the
   * page is already showing, not on a wait that cannot end.
   */
  onStart: () => Promise<void>;
}

export function AdvisoryEvidenceScreen({
  path,
  onBack,
  onStart,
  documents,
}: AdvisoryEvidenceScreenProps) {
  const { t } = useUntypedTranslation();
  const [handoff, setHandoff] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const accept = useCallback(
    (list: FileList | null) => {
      if (!list || list.length === 0) {
        return;
      }
      documents.addFiles(Array.from(list));
    },
    [documents]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      // Both calls are load-bearing: without them the browser navigates away to
      // render the dropped file, discarding the whole wizard.
      event.preventDefault();
      event.stopPropagation();
      setDragging(false);
      accept(event.dataTransfer?.files ?? null);
    },
    [accept]
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLElement>) => {
    // A dragover with no preventDefault means the drop event never fires at all.
    event.preventDefault();
    setDragging(true);
  }, []);

  const handleStart = () => {
    // `handoff` flips FIRST, before awaiting, so the button cannot be pressed a
    // second time while the two turns settle — `send()` aborts any in-flight
    // turn, so a double press would abort the lock the run depends on.
    setHandoff(true);
    void onStart().catch(() => {
      // A rejected start returns the user to this body rather than leaving them
      // on a wait that will never end. The error itself is the page's to render:
      // this component never sees provider text and never should.
      setHandoff(false);
    });
  };

  if (handoff) {
    return <AdvisoryThinkingState titleKey="advisory:thinking.run.title" suppressLeaveNote />;
  }

  const position = stepPosition('evidence', path);
  // `path` is non-nullable and `evidence` is addressable on both paths, so
  // this call is total — no fallback is needed, and any fallback would be a
  // wrong value waiting for a refactor to reach it. `stepPosition`'s return
  // type stays nullable to cover callers that can pass a null `path` (this
  // component's props type excludes that), so the non-null assertions below
  // encode the same guarantee TypeScript cannot infer from the call site
  // alone.

  return (
    <AdvisoryEngagementShell
      position={position!.position}
      total={position!.total}
      eyebrowKey="advisory:evidence.eyebrow"
      titleKey="advisory:evidence.title"
      hintKey="advisory:evidence.hint"
      actions={
        <>
          <Button variant="text" disableRipple onClick={onBack} sx={[pressable, focusRing]}>
            {t('advisory:evidence.backAction')}
          </Button>
          <Button
            variant="contained"
            disableRipple
            data-testid="advisory-evidence-start"
            onClick={handleStart}
            // Closed while any document is still uploading or ingesting. Starting
            // then would produce an engagement that never sees those files, and
            // nothing downstream would report it — the run would simply retrieve
            // less than the user believes it did.
            disabled={documents.blocked}
            aria-describedby={documents.blocked ? 'advisory-evidence-wait' : undefined}
            sx={[pillAction, pressable, focusRing]}
          >
            {t('advisory:evidence.startAction')}
          </Button>
        </>
      }
    >
      <Box
        data-testid="advisory-evidence-dropzone"
        data-dragging={dragging ? 'true' : 'false'}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragging(false)}
        sx={[
          evidenceDropzone,
          // `lavenderDeep` is the same token `focusRing` uses for its accent ring, so
          // the drag affordance reads as the one accent this design already has
          // rather than introducing a second.
          dragging
            ? theme => ({ borderColor: theme.colors.lavenderDeep, borderStyle: 'dashed' })
            : {},
        ]}
      >
        <Typography
          sx={theme => ({
            fontSize: theme.visualPrimitives.dzTitleSize,
            fontWeight: theme.visualPrimitives.dzTitleWeight,
            color: theme.colors.inkPrimary,
          })}
        >
          {t('advisory:evidence.dzTitle')}
        </Typography>
        <Typography
          sx={theme => ({
            marginTop: theme.visualPrimitives.dzSubGap,
            fontSize: theme.visualPrimitives.dzSubSize,
            color: theme.colors.inkTertiary,
          })}
        >
          {t('advisory:evidence.dzSub')}
        </Typography>

        {/* The input is the accessible control; the Box is a convenience surface.
            Hidden visually rather than removed, so keyboard and screen-reader users
            reach the same affordance a mouse user drags onto.

            `accept` is a PICKER HINT and never validation: gmid owns the acceptance
            set (33 MIME types) and its size ceiling is env-configurable, so a
            mirrored list here could only ever drift — and drifting low would refuse
            a file the server would have taken, with no server error to explain it. */}
        <input
          ref={inputRef}
          type="file"
          multiple
          data-testid="advisory-evidence-input"
          aria-label={t('advisory:evidence.dzAction')}
          accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.odt,.ods,.odp,.rtf,.csv,.tsv,.txt,.md,.html,.xml,.json,.yaml,.eml,.msg,.epub,.jpg,.jpeg,.png,.webp,.tiff,.gif"
          onChange={event => {
            accept(event.target.files);
            // Cleared so choosing the SAME file twice fires onChange again — the
            // browser suppresses it otherwise, and a retry-by-reselect would
            // silently do nothing.
            event.target.value = '';
          }}
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        />
        <Button
          variant="text"
          disableRipple
          data-testid="advisory-evidence-choose"
          onClick={() => inputRef.current?.click()}
          sx={[pressable, focusRing, { marginTop: 1 }]}
        >
          {t('advisory:evidence.dzAction')}
        </Button>
        <Typography
          sx={theme => ({
            fontSize: theme.visualPrimitives.dzSubSize,
            color: theme.colors.inkTertiary,
          })}
        >
          {t('advisory:evidence.dzAccepted')}
        </Typography>

        <AdvisoryDocumentList
          documents={documents.documents}
          onRetry={documents.retry}
          onRemove={documents.remove}
        />

        {/* Why the start action is closed, said once and tied to the button by
            aria-describedby rather than left for the user to infer from a disabled
            control. */}
        {documents.blocked ? (
          <Typography
            id="advisory-evidence-wait"
            role="status"
            aria-live="polite"
            data-testid="advisory-evidence-wait"
            sx={theme => ({
              fontSize: theme.visualPrimitives.dzSubSize,
              color: theme.colors.inkTertiary,
            })}
          >
            {t('advisory:evidence.waitForDocuments')}
          </Typography>
        ) : null}

        {/* Stated because the alternative is invisible: with documents present the
            run switches to the doc-intel profile, and with none it does not. */}
        {documents.readyCount > 0 && !documents.blocked ? (
          <Typography
            data-testid="advisory-evidence-will-read"
            sx={theme => ({
              fontSize: theme.visualPrimitives.dzSubSize,
              color: theme.colors.inkTertiary,
            })}
          >
            {t('advisory:evidence.withDocuments', { count: documents.readyCount })}
          </Typography>
        ) : null}
      </Box>
    </AdvisoryEngagementShell>
  );
}

export default AdvisoryEvidenceScreen;
